import { useState, useEffect } from 'react';
import { GROUP_BY } from '../../../../constants';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../utils';

/**
 * localStorage key for caching available group values
 * Enables faster initial renders when returning to the dispatch page
 * by avoiding an extra API call for group options
 */
const AVAILABLE_GROUP_VALUES_KEY = 'dispatch_available_group_values';

/**
 * useDispatchGroupBy - Hook for managing groupBy state and localStorage caching
 *
 * This hook handles:
 * - GroupBy field selection state
 * - Available groupBy fields from API
 * - Selected group items for filtering
 * - localStorage caching of available values
 * - Session restoration state
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.initialState - Initial values from URL
 * @param {string} options.initialState.groupBy - Initial groupBy field
 * @param {string[]} options.initialState.groupItems - Initial selected items
 * @param {boolean} options.initialState.groupApplied - Initial groupApplied flag
 * @param {URLSearchParams} options.searchParams - For checking URL state
 *
 * @returns {Object} GroupBy state, handlers, and utilities
 */
export const useDispatchGroupBy = ({ initialState = {}, searchParams } = {}) => {
  // =====================================================================
  // GROUP BY STATE
  // =====================================================================

  /**
   * Available fields that can be used for grouping
   * Populated from API response (e.g., ['partner_id', 'scheduled_date', 'partner_id,scheduled_date'])
   */
  const [groupArray, setGroupArray] = useState([]);

  /**
   * Currently selected group by field
   * Can be single field ('partner_id') or comma-separated ('partner_id,scheduled_date')
   * Default is GROUP_BY.TITLE which shows "Group By" placeholder text
   */
  const [initialGroupBy] = useState(() => initialState.groupBy || GROUP_BY.TITLE);
  const [groupBy, setGroupByState] = useState(initialGroupBy);

  /**
   * Available values for the selected groupBy field
   * Format: Array of [id, display_name] tuples (e.g., [[1, "Partner A"], [2, "Partner B"]])
   * Fetched via separate API call when groupBy field changes
   */
  const [availableValuesGroupBy, setAvailableValuesGroupBy] = useState([]);

  /**
   * Currently selected group by values
   * When user selects specific groups, only those records are shown
   * Format: Array of [id, name] tuples or raw IDs depending on source
   */
  const [selectedItem, setSelectedItem] = useState(
    initialState.groupItems || [],
  );

  /**
   * Flag indicating whether groupBy filtering is currently active
   * Separate from just having a groupBy field selected - this tracks if it's applied
   */
  const [groupApplied, setGroupApplied] = useState(
    initialState.groupApplied || false,
  );

  /**
   * Flag indicating we're currently restoring from URL params
   * Prevents caching of stale values during restoration
   */
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  // =====================================================================
  // CACHE HELPERS
  // =====================================================================

  /**
   * Loads cached values for a groupBy field from localStorage
   *
   * @param {string} field - The groupBy field to load values for
   * @returns {Array|null} Cached values or null if not found
   */
  const loadCachedValues = (field) => {
    try {
      const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
      if (cachedValues[field] && Array.isArray(cachedValues[field])) {
        return cachedValues[field];
      }
    } catch (error) {
      console.error('Error loading cached group values:', error);
    }
    return null;
  };

  /**
   * Saves values to localStorage cache for a groupBy field
   *
   * @param {string} field - The groupBy field
   * @param {Array} values - The values to cache
   */
  const saveCachedValues = (field, values) => {
    try {
      const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
      const updatedCache = {
        ...cachedValues,
        [field]: values,
      };
      setLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY, updatedCache);
    } catch (error) {
      console.error('Error caching group values:', error);
    }
  };

  // =====================================================================
  // UTILITY FUNCTIONS
  // =====================================================================

  /**
   * Determines if groupBy filtering should be applied to the API request
   *
   * GroupBy is applied when:
   * 1. groupApplied flag is true (user has enabled grouping)
   * 2. groupBy is a valid option in groupArray OR groupBy is being restored from URL
   * 3. Items are selected OR no items were in URL (fresh groupBy selection)
   *
   * @returns {boolean} Whether to include groupBy params in API request
   */
  const shouldApplyGroupBy = () => {
    const urlGroupItems = searchParams?.get('groupItems');
    const urlGroupBy = searchParams?.get('groupBy');
    
    // If restoring from URL, trust the URL state even if groupArray isn't populated yet
    const isValidGroupBy = groupArray.includes(groupBy) || Boolean(urlGroupBy && groupBy === urlGroupBy);
    
    return (
      groupApplied &&
      isValidGroupBy &&
      (selectedItem.length > 0 || !urlGroupItems)
    );
  };

  /**
   * Builds the groupBy portion of the API payload
   * Supports single field or multiple comma-separated fields for grouping
   *
   * @returns {Object} GroupBy payload with field name and specifics
   */
  const buildGroupByPayload = () => {
    const shouldUse = shouldApplyGroupBy();
    let groupbyParams = {
      groupby_field: shouldUse ? groupBy : '',
      groupby_specifics: {},
    };

    if (shouldUse && selectedItem.length) {
      // Handle comma-separated groupBy fields (e.g., 'partner_id,scheduled_date')
      if (groupBy.includes(',')) {
        const fields = groupBy.split(',');
        const groupbySpecifics = {};

        fields.forEach((field) => {
          groupbySpecifics[field.trim()] = selectedItem.map((item) => {
            if (Array.isArray(item) && item.length > 1) {
              const id = item[0];
              return !isNaN(Number(id)) ? Number(id) : id;
            }
            return !isNaN(Number(item)) ? Number(item) : item;
          });
        });

        groupbyParams.groupby_specifics = groupbySpecifics;
      } else {
        // Single groupBy field
        groupbyParams.groupby_specifics = {
          [groupBy]: selectedItem.map((item) => {
            if (Array.isArray(item) && item.length > 1) {
              const id = item[0];
              return !isNaN(Number(id)) ? Number(id) : id;
            }
            return !isNaN(Number(item)) ? Number(item) : item;
          }),
        };
      }
    }

    return groupbyParams;
  };

  // =====================================================================
  // ACTION HANDLERS
  // =====================================================================

  /**
   * Handles changing the groupBy field selection
   *
   * When user selects a different groupBy field:
   * 1. Updates groupBy state
   * 2. Clears previously selected items (they belong to old field)
   * 3. Marks groupBy as applied if it's a valid option
   * 4. Tries to load cached values for faster initial render
   *
   * @param {string} newGroupBy - The new groupBy field name
   */
  const handleGroupByChange = (newGroupBy) => {
    // No-op if selecting same field
    if (newGroupBy === groupBy) return;

    setGroupByState(newGroupBy);
    // Clear selected items since they were for the previous field
    setSelectedItem([]);

    // Auto-apply groupBy when selecting a valid option
    if (groupArray.includes(newGroupBy)) {
      setGroupApplied(true);

      // Try to use cached values for smoother UX while API loads
      const cachedValues = loadCachedValues(newGroupBy);
      if (cachedValues) {
        setAvailableValuesGroupBy(cachedValues);
      }
    }
  };

  /**
   * Removes groupBy filtering and resets to default state
   *
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.onRemove - Called after state is reset
   */
  const removeGroupBy = (callbacks = {}) => {
    setGroupApplied(false);
    setSelectedItem([]);
    setAvailableValuesGroupBy([]);
    setGroupByState(GROUP_BY.TITLE);

    // Call the callback if provided (e.g., to fetch fresh data)
    callbacks.onRemove?.();
  };

  // =====================================================================
  // EFFECTS
  // =====================================================================

  /**
   * Effect: Restore cached group values on mount
   *
   * Loads cached group values from localStorage to enable faster initial render
   * before the API response comes back
   */
  useEffect(() => {
    const currentGroupBy = initialGroupBy;
    const cachedValues = loadCachedValues(currentGroupBy);
    if (cachedValues) {
      setAvailableValuesGroupBy(cachedValues);
    }
  }, [initialGroupBy]);

  /**
   * Effect: Cache available group values to localStorage
   *
   * Saves group values whenever they change so they can be restored on next visit
   * Skips caching during session restoration to avoid overwriting with stale data
   */
  useEffect(() => {
    // Skip caching during initial session restoration
    if (!isRestoringSession && availableValuesGroupBy.length > 0) {
      saveCachedValues(groupBy, availableValuesGroupBy);
    }

    // Mark session restoration as complete after first render
    if (isRestoringSession) {
      setIsRestoringSession(false);
    }
  }, [availableValuesGroupBy, groupBy, isRestoringSession]);

  // =====================================================================
  // RETURN VALUE
  // =====================================================================

  return {
    // GroupBy field state
    groupBy,
    setGroupBy: setGroupByState,
    groupArray,
    setGroupArray,
    groupApplied,
    setGroupApplied,

    // Selected values state
    availableValuesGroupBy,
    setAvailableValuesGroupBy,
    selectedItem,
    setSelectedItem,

    // Session state
    isRestoringSession,

    // Handlers
    handleGroupByChange,
    removeGroupBy,

    // Utilities
    shouldApplyGroupBy,
    buildGroupByPayload,

    // Cache helpers (exposed for useDispatchData)
    loadCachedValues,
    saveCachedValues,
  };
};
